import qs from 'qs'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getImage } from '../../../components/helpers/FormatLinks'
import LoadingIndicator from '../../../components/loadingindicator'
import Page from '../../../components/page/Page'
import { useSharedState } from '../../../components/waxplorer/Waxplorer'
import { Button, Input, Tab, Tabs } from '@nextui-org/react'
import cn from 'classnames'
import SearchComponent from '../../../components/searchitems'
import MiniAssetPreview from '../../../components/assetpreview/MiniAssetPreview'
import { useScreenWidth } from '../../../hooks/useScreenWidth'
import SearchIcon from '@mui/icons-material/Search'
import ErrorNote from '../../../components/common/util/ErrorNote'
import { inputStyles } from '../../../components/common/styles'
import { TradeBreadCrumbs } from '../../../components/trading/TradeBreadCrumbs'

const TradePage = (props) => {
    let values =
        typeof window !== 'undefined'
            ? qs.parse(
                  window.location.search.substring(
                      1,
                      window.location.search.length,
                  ),
              )
            : []

    const { t } = useTranslation('common')

    const screenWidth = useScreenWidth()

    const tabKey = 'new-trade'

    const [state, dispatch] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : values['user']

    const [partner, setPartner] = useState(
        values['partner'] ? values['partner'] : '',
    )

    const initAssetId = values['asset_id']

    const [tempPartner, setTempPartner] = useState(partner)

    const [selected, setSelected] = useState('mine')
    const [error, setError] = useState(null)
    const [transferred, setTransferred] = useState(false)
    const [searchPartnerTimer, setSearchPartnerTimer] = useState(null)

    const [initial, setInitial] = useState(true)

    const [selectedAssets, setSelectedAssets] = useState({})
    const [addedAssets, setAddedAssets] = useState(0)
    const [isLoading, setIsLoading] = useState(false)

    const tradeItemsBoxCn = cn(
        'w-full h-auto dark:border-zinc-700 py-4 border-zinc-300 border-2 min-h-[300px]',
        'mb-5 px-4 rounded-xl bg-zinc-100 dark:bg-[#121212] overflow-hidden',
    )

    const initSelectedAssets = useCallback(
        () => async (assetId) => {
            get('asset/' + assetId).then((res) => {
                selectedAssets[partner] = {}
                selectedAssets[partner][res['assetId']] = {
                    assetId: res['assetId'],
                    collection: res['collection'],
                    image: res['image'],
                    name: res['name'],
                    mint: res['mint'],
                    contract: res['contract'],
                    verified: res['collection']['verification'],
                }
                setSelectedAssets(selectedAssets)
            })
        },
        [],
    )

    useEffect(() => {
        if (partner && initAssetId && initial) {
            setInitial(false)
            initSelectedAssets(initAssetId)
        }

        if (transferred) {
            setSelectedAssets({})
            setAddedAssets(0)
            setIsLoading(false)
            setTransferred(false)
        }
    }, [
        tabKey,
        addedAssets,
        transferred,
        userName,
        initAssetId,
        initSelectedAssets,
        initial,
        partner,
    ])

    const handleTrade = (tradeInfo) => {
        if (tradeInfo) {
            if (tradeInfo['error']) {
                setError(tradeInfo['error'])
                setIsLoading(false)
            }
            if (tradeInfo['transferred']) setTransferred(true)
            else setIsLoading(false)
        } else {
            setIsLoading(false)
        }
    }

    const trade = () => {
        setIsLoading(true)
        dispatch({ type: 'SET_ASSETS', payload: selectedAssets })
        dispatch({
            type: 'SET_CALLBACK',
            payload: (tradeInfo) => handleTrade(tradeInfo),
        })
        dispatch({ type: 'SET_ACTION', payload: 'trade' })
    }

    const addAsset = (user, asset) => {
        if (!selectedAssets[user]) selectedAssets[user] = {}

        selectedAssets[user][asset.assetId] = asset

        setSelectedAssets(selectedAssets)

        setAddedAssets(addedAssets + 1)
    }

    const dismissError = () => {
        setError(null)
    }

    const removeAsset = (name, assetId) => {
        delete selectedAssets[name][assetId]

        setSelectedAssets(selectedAssets)
        setAddedAssets(addedAssets - 1)
    }

    const addedUserAssets =
        selectedAssets[userName] &&
        Object.keys(selectedAssets[userName]).length > 0
            ? Object.keys(selectedAssets[userName]).map((assetId) => (
                  <div
                      className={cn(
                          'relative bg-zinc-50 hover:bg-zinc-100 dark:bg-zinc-900 rounded-lg hover:shadow-xl h-full shadow-lg dark:shadow dark:hover:shadow-bluet  dark:hover:bg-zinc-800 duration-300',
                      )}
                  >
                      <div
                          className={cn(
                              'absolute top-2 right-2 w-6 h-6 z-20 cursor-pointer',
                          )}
                      >
                          <img
                              src="/mini_asset_card/minus.svg"
                              alt="X"
                              onClick={() => removeAsset(userName, assetId)}
                          />
                      </div>
                      <MiniAssetPreview
                          asset={selectedAssets[userName][assetId]}
                      />
                  </div>
              ))
            : ''

    const addedPartnerAssets =
        selectedAssets[partner] &&
        Object.keys(selectedAssets[partner]).length > 0
            ? Object.keys(selectedAssets[partner]).map((assetId) => (
                  <div
                      className={cn(
                          'relative bg-zinc-50 hover:bg-zinc-100 dark:bg-zinc-900 rounded-lg hover:shadow-xl flex flex-col h-full shadow-lg dark:shadow dark:hover:shadow-bluet  dark:hover:bg-zinc-800 duration-300',
                      )}
                  >
                      <div
                          className={cn(
                              'absolute top-2 right-2 w-6 h-6 z-20 cursor-pointer',
                          )}
                      >
                          <img
                              src="/mini_asset_card/minus.svg"
                              alt="X"
                              onClick={() => removeAsset(partner, assetId)}
                          />
                      </div>
                      <MiniAssetPreview
                          asset={selectedAssets[partner][assetId]}
                      />
                  </div>
              ))
            : ''

    const handlePartnerChange = (e) => {
        if (searchPartnerTimer) {
            clearInterval(searchPartnerTimer)
        }
        const val = e.target.value

        setTempPartner(val)

        setSearchPartnerTimer(
            setTimeout(function () {
                selectedAssets[val] = {}
                setSelectedAssets(selectedAssets)
                setAddedAssets(
                    (selectedAssets[userName]
                        ? Object.keys(selectedAssets[userName]).length
                        : 0) + Object.keys(selectedAssets[val]).length,
                )
                setPartner(val)
            }, 1000),
        )
    }

    const image = getImage(userName)

    return (
        <Page id="TradePage" image={image} banners={false} {...props}>
            <div className="mx-2 sm:mx-[4vw]">
                <div className="flex flex-row items-center justify-between flex-1">
                    <div>
                        <TradeBreadCrumbs
                            items={[
                                {
                                    href: '/trade',
                                    name: t('navigation.trading'),
                                },
                                {
                                    href: '/trade/new',
                                    name: t('trading.new_trade_offer'),
                                },
                            ]}
                        />

                        <h2 className="mt-4 mb-4 text-2xl text-black xs1:text-3xl sm:text-4xl dark:text-white">
                            {t('trading.new_trade_offer')}
                        </h2>
                    </div>
                </div>

                <div className={'flex w-full justify-end'}>
                    {error ? (
                        <ErrorNote onClick={dismissError} error={error} />
                    ) : (
                        <Button
                            isLoading={isLoading}
                            size="lg"
                            spinner={
                                <svg
                                    className="w-5 h-5 text-current animate-spin"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        fill="currentColor"
                                    />
                                </svg>
                            }
                            radius="sm"
                            isDisabled={
                                isLoading ||
                                addedUserAssets.length === 0 ||
                                addedPartnerAssets.length === 0
                                    ? 'disabled'
                                    : ''
                            }
                            onClick={trade}
                            className={cn(
                                ' disabled:border-neutral disabled:dark:!text-white disabled:text-zinc-800 dark:disabled:bg-zinc-800 disabled:bg-zinc-200 bg-primary text-black text-sm mt-2',
                            )}
                        >
                            <b>{t('trade.send_offer')}</b>
                        </Button>
                    )}
                </div>
                <div className="flex flex-col w-full mt-4">
                    <Tabs
                        key={tabKey}
                        variant="underlined"
                        aria-label="Tabs variants"
                        size="lg"
                        selectedKey={selected}
                        onSelectionChange={(k) => {
                            setSelected(k)
                        }}
                        classNames={{
                            tabList:
                                'gap-6 w-full relative rounded-none p-0 border-b border-divider',
                            cursor: 'w-full bg-primary',
                            tab: 'max-w-fit px-8 h-16 font-semibold text-xs xs1:text-sm sm:text-base',
                            tabContent:
                                'group-data-[selected=true]:text-primary',
                        }}
                    >
                        <Tab key="new-trade" title="Mine">
                            <div className="mt-4">
                                <div className={cn('m-auto')}>
                                    <div
                                        className={cn(
                                            'w-full font-bold sm:h-[64px] h-[48px]',
                                        )}
                                    >
                                        {userName}
                                    </div>
                                </div>

                                <div className={tradeItemsBoxCn}>
                                    <div
                                        className={
                                            'grid grid-flow-col gap-4 overflow-auto h-full items-start justify-start '
                                        }
                                    >
                                        {addedUserAssets}
                                    </div>
                                </div>
                                <div className="">
                                    {userName && (
                                        <SearchComponent
                                            // trade={true}
                                            {...props}
                                            searchSettings={{
                                                owner: {
                                                    value: userName,
                                                    fixed: true,
                                                },
                                                search_type: {
                                                    value: 'tradable',
                                                    fixed: true,
                                                },
                                                order_by: {
                                                    value: 'date_desc',
                                                    fixed: false,
                                                },
                                                minimal: false,
                                                tab: 'new-trade',
                                                page_component: false,
                                                limit: {
                                                    value: 30,
                                                    fixed: true,
                                                },
                                            }}
                                            limit={30}
                                            tab={tabKey}
                                            onAdd={(asset) =>
                                                addAsset(userName, asset)
                                            }
                                            selectedAssets={
                                                selectedAssets[userName]
                                                    ? Object.keys(
                                                          selectedAssets[
                                                              userName
                                                          ],
                                                      )
                                                    : []
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </Tab>
                        <Tab key="recipents" title={t('trade.recipient')}>
                            <div>
                                <div className="sm:max-w-[300px] max-w-full mb-4">
                                    <Input
                                        size={screenWidth < 640 ? 'sm' : 'lg'}
                                        variant="faded"
                                        type="text"
                                        value={tempPartner}
                                        className="text-zinc-400"
                                        classNames={{
                                            ...inputStyles,
                                        }}
                                        name="partner"
                                        label={t('trade.trade_partner')}
                                        placeholder={t('trade.trade_partner')}
                                        onChange={(e) => handlePartnerChange(e)}
                                        disabled={isLoading ? 'disabled' : ''}
                                        endContent={
                                            <SearchIcon className="mb-1 pointer-events-none text-zinc-400 md:mb-2" />
                                        }
                                    />
                                </div>

                                <div className={tradeItemsBoxCn}>
                                    <div
                                        className={
                                            'grid grid-flow-col gap-4 overflow-auto h-full items-start justify-start '
                                        }
                                    >
                                        {addedPartnerAssets}
                                    </div>
                                </div>
                                <div className="">
                                    {partner &&
                                    tempPartner === partner &&
                                    userName !== partner ? (
                                        <SearchComponent
                                            // trade={true}
                                            {...props}
                                            searchSettings={{
                                                owner: {
                                                    value: partner,
                                                    fixed: true,
                                                },
                                                search_type: {
                                                    value: 'tradable',
                                                    fixed: true,
                                                },
                                                order_by: {
                                                    value: 'date_desc',
                                                    fixed: false,
                                                },
                                                minimal: false,
                                                tab: 'new-trade',
                                                page_component: false,
                                                limit: {
                                                    value: 30,
                                                    fixed: true,
                                                },
                                            }}
                                            limit={30}
                                            // tab={tabKey}
                                            onAdd={(asset) =>
                                                addAsset(partner, asset)
                                            }
                                            selectedAssets={
                                                selectedAssets[partner]
                                                    ? Object.keys(
                                                          selectedAssets[
                                                              partner
                                                          ],
                                                      )
                                                    : []
                                            }
                                        />
                                    ) : (
                                        (partner || tempPartner) &&
                                        tempPartner !== partner && (
                                            <div>
                                                <LoadingIndicator />
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </Tab>
                    </Tabs>
                </div>
            </div>
        </Page>
    )
}

TradePage.getInitialProps = async (ctx) => {
    const paths = ctx.asPath.split('/')

    const values = qs.parse(paths[paths.length - 1].replace('trade?', ''))

    values['search_page'] = true
    values['searchType'] = values['search_type']
        ? values['search_type']
        : 'all_assets'

    return values
}

export default TradePage
